// react-redux imports
import { useState } from "react";
import { useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";
import { RootState } from "app/store";
import { CalculatorResult } from "features/auth/types";

import { Sector, SECTORS } from "../constants/sectors";

// styled-components imports
import Section from "../styled-components/Section";
import SectionContent from "../styled-components/SectionContent";
import SectionTitle from "../styled-components/SectionTitle";
import SectionImage from "../styled-components/SectionImage";
import SectionTitleSeparator from "../styled-components/SectionTitleSeparator";
import Global from "./Global";
import Pagination from "./Pagination";
import Energy from "./Energy";
import HouseholdHabits from "./HouseholdHabits";
import Nutrition from "./Nutrition";
import Transport from "./Transport";

interface SectorsProps {
  result: CalculatorResult;
}

function Sectors({ result }: SectorsProps) {
  const [animate, setAnimate] = useState<boolean>(false);
  const [sector, setSector] = useState<Sector>(SECTORS[0]);

  const upDesktopScreen = useSelector(
    (state: RootState) => state.app.upDesktopScreen
  );

  function nextSector() {
    const nextIndex = SECTORS.indexOf(sector) + 1;
    if (nextIndex < SECTORS.length) {
      setAnimate(true);
      setSector(SECTORS[nextIndex]);
    }
  }

  function previousSector() {
    const previousIndex = SECTORS.indexOf(sector) - 1;
    if (!(previousIndex < 0)) {
      setAnimate(true);
      setSector(SECTORS[previousIndex]);
    }
  }

  const sectorComponents: React.ReactNode[] = [
    <Global result={result} />,
    <Transport
      averageSectorFootprint={result.averageFootprint.transport}
      userSectorFootprint={result.userFootprint.transport}
      averageFootprint={result.averageFootprint.total}
      userFootprint={result.userFootprint.total}
    />,
    <Energy
      averageSectorFootprint={result.averageFootprint.energy}
      userSectorFootprint={result.userFootprint.energy}
      averageFootprint={result.averageFootprint.total}
      userFootprint={result.userFootprint.total}
    />,
    <Nutrition
      averageSectorFootprint={result.averageFootprint.nutrition}
      userSectorFootprint={result.userFootprint.nutrition}
      userFootprint={result.userFootprint.total}
      averageFootprint={result.averageFootprint.total}
    />,
    <HouseholdHabits
      averageSectorFootprint={result.averageFootprint.householdHabits}
      userSectorFootprint={result.userFootprint.householdHabits}
      averageFootprint={result.averageFootprint.total}
      userFootprint={result.userFootprint.total}
    />,
  ];

  return (
    <Section>
      <CSSTransition
        timeout={500}
        classNames="sector"
        in={animate}
        onEntered={() => setAnimate(false)}
      >
        <SectionContent>
          <SectionTitle position="left">
            Tu huella en sectores
            {upDesktopScreen && <SectionTitleSeparator position="left" />}
          </SectionTitle>
          <Pagination
            sector={sector}
            page={SECTORS.indexOf(sector) + 1}
            total={SECTORS.length}
            handleNext={nextSector}
            handleBack={previousSector}
          >
            {sectorComponents[SECTORS.indexOf(sector)]}
          </Pagination>
        </SectionContent>
      </CSSTransition>
      <SectionImage
        basis={upDesktopScreen ? "57%" : "255px"}
        src={sector.image}
      />
    </Section>
  );
}

export default Sectors;
